import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { listarPosicoes, compartilharPosicao, pararCompartilhamento as pararCompartilhamentoApi } from "../api/geolocalizacao";

export interface Posicao {
  usuario_id: number;
  nome?: string;
  role?: string;
  lat: number;
  lon: number;
  precisao?: number | null;
  atualizado_em: string;
}

interface GeolocalizacaoContextValue {
  posicoes: Posicao[];
  aoVivo: boolean;
  compartilhando: boolean;
  erroCompartilhamento: string;
  iniciarCompartilhamento: () => void;
  pararCompartilhamento: () => void;
}

const GeolocalizacaoContext = createContext<GeolocalizacaoContextValue | null>(null);

const PAPEIS_COM_ACESSO = ["admin", "candidato", "lideranca", "assessor"];

// Distância aproximada em metros — só usada pra decidir se vale a pena mandar
// uma atualização de posição (movimento pequeno demais não precisa avisar).
function distanciaMetros(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function GeolocalizacaoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const temAcesso = !!user && PAPEIS_COM_ACESSO.includes(user.role);

  const [posicoesPorUsuario, setPosicoesPorUsuario] = useState<Record<number, Posicao>>({});
  const [aoVivo, setAoVivo] = useState(false);
  const [compartilhando, setCompartilhando] = useState(false);
  const [erroCompartilhamento, setErroCompartilhamento] = useState("");
  const watchIdRef = useRef<number | null>(null);
  const ultimoEnvioRef = useRef<{ lat: number; lon: number; ts: number } | null>(null);

  useEffect(() => {
    if (!temAcesso) return;
    listarPosicoes().then((lista: Posicao[]) => {
      const mapa: Record<number, Posicao> = {};
      lista.forEach((p) => (mapa[p.usuario_id] = p));
      setPosicoesPorUsuario(mapa);
    });
  }, [temAcesso]);

  useEffect(() => {
    if (!temAcesso) return;
    const origem = new EventSource("/api/geolocalizacao/eventos");

    origem.onopen = () => setAoVivo(true);
    origem.onerror = () => setAoVivo(false);

    origem.addEventListener("posicao", (e) => {
      const p: Posicao = JSON.parse((e as MessageEvent).data);
      setPosicoesPorUsuario((prev) => ({ ...prev, [p.usuario_id]: p }));
    });

    origem.addEventListener("saiu", (e) => {
      const { usuario_id } = JSON.parse((e as MessageEvent).data);
      setPosicoesPorUsuario((prev) => {
        const { [usuario_id]: _removido, ...resto } = prev;
        return resto;
      });
    });

    return () => origem.close();
  }, [temAcesso]);

  // Só ativo enquanto o usuário liga manualmente nessa sessão — não persiste
  // entre recarregamentos nem roda em segundo plano sem essa ação explícita.
  const iniciarCompartilhamento = () => {
    if (!navigator.geolocation) {
      setErroCompartilhamento("Geolocalização não é suportada neste navegador.");
      return;
    }
    setErroCompartilhamento("");
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setCompartilhando(true);
        const agora = Date.now();
        const ultimo = ultimoEnvioRef.current;
        const moveu =
          !ultimo || distanciaMetros(ultimo.lat, ultimo.lon, pos.coords.latitude, pos.coords.longitude) > 30;
        const tempoPassou = !ultimo || agora - ultimo.ts > 20000;
        if (!moveu && !tempoPassou) return;
        ultimoEnvioRef.current = { lat: pos.coords.latitude, lon: pos.coords.longitude, ts: agora };
        compartilharPosicao({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          precisao: pos.coords.accuracy,
        }).catch(() => {});
      },
      (err) => {
        setErroCompartilhamento(err.message || "Não foi possível obter sua localização.");
        setCompartilhando(false);
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    watchIdRef.current = id;
  };

  const pararCompartilhamento = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    ultimoEnvioRef.current = null;
    setCompartilhando(false);
    pararCompartilhamentoApi().catch(() => {});
  };

  // Se o navegador ainda tiver o watch ligado quando o app desmontar, cancela.
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  return (
    <GeolocalizacaoContext.Provider
      value={{
        posicoes: Object.values(posicoesPorUsuario),
        aoVivo,
        compartilhando,
        erroCompartilhamento,
        iniciarCompartilhamento,
        pararCompartilhamento,
      }}
    >
      {children}
    </GeolocalizacaoContext.Provider>
  );
}

export function useGeolocalizacao() {
  const ctx = useContext(GeolocalizacaoContext);
  if (!ctx) throw new Error("useGeolocalizacao deve ser usado dentro de GeolocalizacaoProvider");
  return ctx;
}
